import React, { useEffect, useState, useMemo } from "react";
import SummaryApi from "../common";

// ────────────────────────────────────────────────
// THEME — same rules as other files
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',        // #172C57
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionRed: '#EF4444',
  actionRedHover: '#DC2626',
};

const CallDuration = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateFilter, setDateFilter] = useState("All");
  const [district, setDistrict] = useState("All");

  const headers = {
    Authorization: "Bearer sandeep_university_916",
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(SummaryApi.getCallDuration3min.url, {
          method: "GET",
          headers,
        });
        const result = await response.json();
        if (result.status) {
          setData(result.data);
          setFiltered(result.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const districts = useMemo(() => {
    return [
      "All",
      ...Array.from(
        new Set(data.map((item) => item.district?.trim()).filter(Boolean))
      ),
    ];
  }, [data]);

  const handleDateFilterChange = (value) => {
    setDateFilter(value);

    const today = new Date();
    let start = "";
    let end = "";

    switch (value) {
      case "Today":
        start = end = today.toISOString().split("T")[0];
        break;
      case "Yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        start = end = yesterday.toISOString().split("T")[0];
        break;
      case "Last 7 Days":
        const last7 = new Date(today);
        last7.setDate(today.getDate() - 6);
        start = last7.toISOString().split("T")[0];
        end = today.toISOString().split("T")[0];
        break;
      case "Last 30 Days":
        const last30 = new Date(today);
        last30.setDate(today.getDate() - 29);
        start = last30.toISOString().split("T")[0];
        end = today.toISOString().split("T")[0];
        break;
      case "This Month":
        start = new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split("T")[0];
        end = today.toISOString().split("T")[0];
        break;
      case "Last Month":
        const firstDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        const lastDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );
        start = firstDayLastMonth.toISOString().split("T")[0];
        end = lastDayLastMonth.toISOString().split("T")[0];
        break;
      case "All":
        start = end = "";
        break;
      default:
        break;
    }

    setStartDate(start);
    setEndDate(end);
  };

  useEffect(() => {
    let temp = [...data];

    if (filter !== "All") {
      temp = temp.filter(
        (s) =>
          s.is_interested?.trim().toLowerCase() === filter.trim().toLowerCase()
      );
    }

    if (district !== "All") {
      temp = temp.filter(
        (s) =>
          s.district?.trim().toLowerCase() === district.trim().toLowerCase()
      );
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setDate(end.getDate() + 1);

      temp = temp.filter((s) => {
        const recordDate = new Date(s.created_date);
        return recordDate >= start && recordDate < end;
      });
    }

    if (search) {
      const lowerSearch = search.toLowerCase();
      temp = temp.filter(
        (s) =>
          s.student_name?.toLowerCase().includes(lowerSearch) ||
          s.team_lead_name?.toLowerCase().includes(lowerSearch) ||
          s.assigned_employee_name?.toLowerCase().includes(lowerSearch) ||
          s.clg_name?.toLowerCase().includes(lowerSearch) ||
          s.mobile_number1?.includes(search)
      );
    }

    setFiltered(temp);
    setCurrentPage(1);
  }, [search, filter, district, startDate, endDate, data]);

  const totalRecords = filtered.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filtered.slice(indexOfFirstRecord, indexOfLastRecord);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 pb-4 border-b border-gray-200">
          <h1
            className="text-2xl md:text-3xl font-bold"
            style={{ color: THEME.primary }}
          >
            Students More Than 3 min Duration
          </h1>
        </div>

        <div className="flex flex-wrap justify-between gap-4 mb-4">
          {/* Call Status */}
          <div className="flex items-center gap-2">
            <label
              className="font-semibold text-sm"
              style={{ color: THEME.primary }}
            >
              Select Call Status :
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ color: THEME.primary }}
              className="border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] transition-all duration-150 outline-none cursor-pointer"
            >
              {[
                "All",
                "Not Interested",
                "Interested",
                "Most Interested",
                "Call Back",
                "Wrong Number",
                "Switched Off",
              ].map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {/* District */}
          <div className="flex items-center gap-2">
            <label
              className="font-semibold text-sm"
              style={{ color: THEME.primary }}
            >
              Select District :
            </label>
            <select
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              style={{ color: THEME.primary }}
              className="border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] transition-all duration-150 outline-none cursor-pointer min-w-[150px]"
            >
              {districts.map((dist) => (
                <option key={dist} value={dist}>
                  {dist}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <label
              className="font-semibold text-sm"
              style={{ color: THEME.primary }}
            >
              Select Date :
            </label>

            <select
              value={dateFilter}
              onChange={(e) => handleDateFilterChange(e.target.value)}
              style={{ color: THEME.primary }}
              className="border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] transition-all duration-150 outline-none cursor-pointer min-w-[160px]"
            >
              <option value="All">All</option>
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="Last 7 Days">Last 7 Days</option>
              <option value="Last 30 Days">Last 30 Days</option>
              <option value="This Month">This Month</option>
              <option value="Last Month">Last Month</option>
              <option value="Custom Range">Custom Range</option>
            </select>

            {dateFilter === "Custom Range" && (
              <>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] transition-all duration-150 outline-none cursor-pointer"
                  title="Start Date"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] transition-all duration-150 outline-none cursor-pointer"
                  title="End Date"
                />
              </>
            )}
          </div>
        </div>

        {/* Search + Clear */}
        <div className="flex flex-wrap gap-4 mb-6 items-center">
          <div className="relative flex-1 min-w-[280px] max-w-2xl">
            <input
              type="text"
              placeholder="Search by student, employee, team lead, college, or mobile number..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57] transition-all duration-150 outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-6a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </div>

          <button
            onClick={() => {
              setSearch("");
              setFilter("All");
              setDistrict("All");
              setStartDate("");
              setEndDate("");
              setDateFilter("All");
            }}
            style={{ backgroundColor: THEME.actionRed }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRedHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionRed)}
            className="px-5 py-2 text-white rounded-lg transition-colors duration-200 shadow-sm outline-none font-semibold text-sm"
          >
            Clear Filters
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div
            className="text-center py-12 text-xl font-medium"
            style={{ color: THEME.primary }}
          >
            Loading data...
            <span className="animate-pulse ml-2">...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-red-500 text-xl font-medium">
            No records found matching your criteria.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200">
            <table className="min-w-full text-sm text-left text-gray-700">
              <thead className="bg-gray-200">
                <tr>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">SNO</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Enrollment Code</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Student Name</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">District</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">City</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">College Name</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Mobile</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Call Status</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Call Duration</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Enrolled Date</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Employee Name</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Team Lead Name</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRecords.map((item, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-gray-50 hover:bg-slate-100 transition-colors" : "hover:bg-slate-100 transition-colors"}
                  >
                    <td className="px-4 py-2 border text-slate-600">
                      {indexOfFirstRecord + index + 1}
                    </td>
                    <td className="px-4 py-2 border">{item.enrollment_code}</td>
                    <td className="px-4 py-2 border font-medium text-slate-800">
                      {item.student_name}
                    </td>
                    <td className="px-4 py-2 border">{item.district}</td>
                    <td className="px-4 py-2 border">{item.city}</td>
                    <td className="px-4 py-2 border">{item.clg_name}</td>
                    <td className="px-4 py-2 border">{item.mobile_number1}</td>
                    <td className="px-4 py-2 border">{item.is_interested}</td>
                    <td className="px-4 py-2 border">
                      {item.call_duration?.split(".")[0]}
                    </td>
                    <td className="px-4 py-2 border">{item.created_date}</td>
                    <td className="px-4 py-2 border">{item.assigned_employee_name}</td>
                    <td className="px-4 py-2 border">{item.team_lead_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4">
            <div>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ color: THEME.primary }}
                className="px-3 py-1 border border-gray-300 rounded-lg mr-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-semibold text-sm"
              >
                Prev
              </button>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ color: THEME.primary }}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-semibold text-sm"
              >
                Next
              </button>
            </div>
            <div
              className="text-sm font-semibold"
              style={{ color: THEME.primary }}
            >
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span
                className="font-semibold"
                style={{ color: THEME.primary }}
              >
                Records per page:
              </span>
              <select
                value={recordsPerPage}
                onChange={handleRecordsPerPageChange}
                style={{ color: THEME.primary }}
                className="border border-gray-300 rounded-lg px-2 py-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#172C57]"
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallDuration;